import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import axios from "axios";
import { getHost } from "../../../reducers/utils";
import { suggestiontosearch } from "../../../reducers/api";
import { setAlternativeRegionName } from "../../../reducers/offersSlice";
import { includesNormalized, matchesAllWords, matchesAnyWord, normalizeForSearch, toSearchWords } from "../../../utils/searchNormalize";
import "./SearchPopUp.scss";
import { IoClose, IoSearch } from "react-icons/io5";
import { FaCity, FaHome, FaMapMarkerAlt, FaMapPin } from "react-icons/fa";

// ✅ Stable empty array reference for useSelector fallback (prevents infinite re-renders)
const EMPTY_ARRAY = [];

axios.defaults.headers.common["AgencyID"] = window.configData.AGENCYID;
axios.defaults.headers.common["Version"] = import.meta.env.VITE_APP_VERSION;
axios.defaults.headers.common["App"] = window.configData.APP;

const SearchPopUp = ({ isOpen, onClose, onSelect, activeMode = "PAC" }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);

  const debounceRef = useRef(null);

  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadedOptions, setLoadedOptions] = useState([]);

  // Cache: Amadeus-Rohdaten + Suchbegriff bei dem sie geladen wurden
  const cachedResultsRef = useRef({ query: "", matches: [], regionGroups: [] });

  // Banner States
  const [suggestionBanners, setSuggestionBanners] = useState([]);
  const [popularDestinationBanners, setPopularDestinationBanners] = useState([]);
  // Track which mode banners were loaded for (lazy loading + cache)
  const [loadedForMode, setLoadedForMode] = useState(null);

  const agencyproperties = useSelector((state) => state.properties.agencyproperties);

  const regionGroups = useSelector((state) => state.properties?.regionGroups || EMPTY_ARRAY);

  // Get session_id from Redux for banner personalization
  const sessionId = useSelector((state) => state.system?.sessionId);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };

    // Delay adding the listener to avoid catching the same mousedown event that opened the popup
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Clear banner cache when activeMode changes
  useEffect(() => {
    if (loadedForMode !== null && loadedForMode !== activeMode) {
      setSuggestionBanners([]);
      setPopularDestinationBanners([]);
      setLoadedForMode(null);
    }
  }, [activeMode, loadedForMode]);

  // Lazy load banners: Only fetch on FIRST open (per mode)
  // This allows backend time to process session data (fire & forget)
  useEffect(() => {
    if (!isOpen) return;
    if (loadedForMode === activeMode) return; // Already loaded for this mode

    const fetchBanners = async () => {
      try {
        const tenantId = window.configData?.AGENCYID;
        if (!tenantId) {
          console.warn("[SearchPopUp] No AGENCYID found, skipping banner fetch");
          return;
        }

        let apiCall = "publicbanners";

        if (activeMode == "LTA") {
          apiCall = "publicevents";
        }

        const apiUrl = getHost();

        // Fetch suggestion banners (Info-Cards)
        // Pass session_id for personalization and search_mode for filtering
        try {
          const suggestionResponse = await axios.get(`${apiUrl}/${apiCall}`, {
            params: {
              tenant_id: tenantId,
              type: "suggestion",
              session_id: sessionId || undefined,
              active_mode: activeMode || undefined,
            },
          });

          if (suggestionResponse.data?.code === 200 && Array.isArray(suggestionResponse.data?.data)) {
            setSuggestionBanners(suggestionResponse.data.data);
          }
        } catch (err) {
          console.error("[SearchPopUp] Failed to fetch suggestion banners:", err);
        }

        // Fetch popular destination banners (Image-Cards)
        // Pass session_id for personalization and search_mode for filtering
        try {
          const destinationResponse = await axios.get(`${apiUrl}/${apiCall}`, {
            params: {
              tenant_id: tenantId,
              type: "populardestination",
              session_id: sessionId || undefined,
              active_mode: activeMode || undefined,
            },
          });

          if (destinationResponse.data?.code === 200 && Array.isArray(destinationResponse.data?.data)) {
            setPopularDestinationBanners(destinationResponse.data.data);
          }
        } catch (err) {
          console.error("[SearchPopUp] Failed to fetch popular destination banners:", err);
        }

        // Mark as loaded for this mode (cache)
        setLoadedForMode(activeMode);
      } catch (error) {
        console.error("[SearchPopUp] Banner fetch error:", error);
      }
    };

    fetchBanners();
  }, [isOpen, activeMode, sessionId, loadedForMode]);

  // Amadeus-Rohdaten in typisierte Arrays umwandeln
  const parseMatches = (matches) => {
    const regions = [],
      cities = [],
      hotels = [];
    for (const suggest of matches) {
      const obj = { label: suggest.name, Name: suggest.name, value: suggest.name };
      if (suggest.type === "Village") {
        obj.CityID = suggest.id * 1;
        cities.push(obj);
      } else if (suggest.type === "Region" && suggest.iffRegionCode) {
        obj.RegionID = suggest.iffRegionCode * 1;
        regions.push(obj);
      } else if (suggest.type === "Accommodation") {
        obj.HotelID = suggest.id * 1;
        obj.villageName = suggest.villageName;
        obj.iffVillageCode = suggest.iffVillageCode;
        hotels.push(obj);
      } else if (suggest.type === "Destination") {
        obj.RegionID = suggest.iffRegionCode;
        regions.push(obj);
      }
    }
    return { regions, cities, hotels };
  };

  // Aus typisierten Arrays + Filter die sortierten Gruppen bauen
  // Wieviele Eintraege stecken im gecachten Serverergebnis?
  const zaehleParsed = (parsed) =>
    (parsed?.hotels?.length || 0) + (parsed?.regions?.length || 0) + (parsed?.cities?.length || 0);

  // Wieviele davon ueberstehen den strengen Wortfilter (ohne den some()-Fallback)?
  // Nur das beantwortet die Frage, ob das Weitertippen die Liste eingrenzt.
  const zaehleGefiltert = (parsed, filterLower) => {
    const words = toSearchWords(filterLower);
    const passt = (text) => matchesAllWords(text || "", words);
    return (
      (parsed?.hotels || []).filter((h) => passt(h.label + " " + (h.villageName || ""))).length +
      (parsed?.regions || []).filter((r) => passt(r.label)).length +
      (parsed?.cities || []).filter((c) => passt(c.label + " " + (c.villageName || ""))).length
    );
  };

  const buildGroups = (parsed, filterLower, regionGroupsFiltered) => {
    const groups = [];
    if (regionGroupsFiltered.length > 0 && activeMode !== "LTA") {
      groups.push({
        label: intl.formatMessage({ id: "travelGroups" }),
        options: regionGroupsFiltered,
      });
    }

    // Akzent-agnostisch: Nutzer tippen "espana"/"malaga", die Daten heissen
    // "España"/"Málaga". Mit reinem includes() fiel genau der gesuchte Treffer
    // raus - und weil dann NICHTS mehr passte, griff der some()-Fallback unten
    // und ersetzte die Relevanzsortierung durch eine alphabetische Liste.
    const searchWords = toSearchWords(filterLower);
    const allWordsMatch = (text) => matchesAllWords(text || "", searchWords);
    const nameMatchesSearch = (name) => allWordsMatch(name || "");

    // Lokale Filterung: Name oder Name+Ort muss alle Suchwoerter enthalten
    let filteredHotels = parsed.hotels.filter((h) => allWordsMatch(h.label + " " + (h.villageName || "")));
    let filteredRegions = parsed.regions.filter((r) => allWordsMatch(r.label));
    let filteredCities = parsed.cities.filter((c) => allWordsMatch(c.label + " " + (c.villageName || "")));

    // Falls lokale Filterung zu streng: auch Hotels zeigen die nur im Namen teilweise matchen
    if (filteredHotels.length === 0 && parsed.hotels.length > 0) {
      filteredHotels = parsed.hotels.filter((h) => matchesAnyWord(h.label || "", searchWords));
    }

    // Hotels sortieren: best match nach oben, dann alphabetisch
    filteredHotels.sort((a, b) => {
      const aFull = allWordsMatch(a.label + " " + (a.villageName || ""));
      const bFull = allWordsMatch(b.label + " " + (b.villageName || ""));
      if (aFull && !bFull) return -1;
      if (!aFull && bFull) return 1;
      return a.label.localeCompare(b.label);
    });
    filteredRegions.sort((a, b) => a.label.localeCompare(b.label));
    filteredCities.sort((a, b) => a.label.localeCompare(b.label));

    const directRegions = filteredRegions.filter((o) => nameMatchesSearch(o.label));
    const indirectRegions = filteredRegions.filter((o) => !nameMatchesSearch(o.label));
    const directCities = filteredCities.filter((o) => nameMatchesSearch(o.label));
    const indirectCities = filteredCities.filter((o) => !nameMatchesSearch(o.label));

    const hotelsDominant = filteredHotels.length > 0 && directRegions.length === 0 && directCities.length === 0;

    if (hotelsDominant) {
      groups.push({ label: intl.formatMessage({ id: "hotels", defaultMessage: "Hotels" }), options: filteredHotels });
      if (indirectCities.length > 0) groups.push({ label: intl.formatMessage({ id: "resortsWithMatchingHotels", defaultMessage: "Orte mit passenden Hotels" }), options: indirectCities });
      if (indirectRegions.length > 0) groups.push({ label: intl.formatMessage({ id: "regionsWithMatchingHotels", defaultMessage: "Regionen mit passenden Hotels" }), options: indirectRegions });
    } else {
      if (directRegions.length > 0) groups.push({ label: intl.formatMessage({ id: "regions" }), options: directRegions });
      if (directCities.length > 0) groups.push({ label: intl.formatMessage({ id: "resorts" }), options: directCities });
      if (filteredHotels.length > 0) groups.push({ label: intl.formatMessage({ id: "hotels", defaultMessage: "Hotels" }), options: filteredHotels });
      if (indirectRegions.length > 0) groups.push({ label: intl.formatMessage({ id: "regionsWithMatchingHotels", defaultMessage: "Regionen mit passenden Hotels" }), options: indirectRegions });
      if (indirectCities.length > 0) groups.push({ label: intl.formatMessage({ id: "resortsWithMatchingHotels", defaultMessage: "Orte mit passenden Hotels" }), options: indirectCities });
    }
    return groups;
  };

  const loadOptions = (destinationFilter) => {
    if (destinationFilter.length === 0) {
      setLoadedOptions([]);
      return;
    }

    const filterLower = destinationFilter.toLowerCase();

    // RegionGroups lokal filtern
    let regionGroupsFiltered = [];
    if (Array.isArray(regionGroups) && regionGroups.length > 0) {
      regionGroupsFiltered = regionGroups.map((rg) => ({ value: rg.Name, label: rg.Name, ...rg })).filter((rg) => includesNormalized(rg.value || "", filterLower));
    }

    // Cache-Hit: nur wenn Amadeus damals ALLES geliefert hat (nicht am 100er-Limit war)
    // Sonst riskiert lokales Filtern, dass spezifischere Treffer fehlen (z.B. "b" → 100 b-Sachen, "belek" findet nichts)
    const cached = cachedResultsRef.current;
    if (cached.query && cached.query.length >= 3 && cached.matches.length > 0 && cached.matches.length < 100 && normalizeForSearch(filterLower).startsWith(normalizeForSearch(cached.query))) {
      // Der Cache darf nur gewinnen, wenn das Weitertippen die Liste auch wirklich
      // eingrenzt. Trifft der strenge Wortfilter nichts mehr, wuerde buildGroups auf
      // den some()-Fallback zurueckfallen ("irgendein Wort genuegt") und statt einer
      // Trefferliste eine alphabetische Restmenge zeigen. Grenzt er gar nicht ein,
      // bringt der Cache ohnehin nichts. In beiden Faellen lieber den Server fragen -
      // der hat die vollstaendigen Daten und die bessere Relevanzsortierung.
      const nachFilter = zaehleGefiltert(cached.parsed, filterLower);
      const imCache = zaehleParsed(cached.parsed);
      // Unveraenderte Eingabe (z.B. loadOptions laeuft wegen nachgeladener
      // RegionGroups erneut): dasselbe Ergebnis, kein Grund neu zu fragen.
      const gleicheEingabe = normalizeForSearch(filterLower) === normalizeForSearch(cached.query);

      if (gleicheEingabe || (nachFilter > 0 && nachFilter < imCache)) {
        setLoadedOptions(buildGroups(cached.parsed, filterLower, regionGroupsFiltered));
        return;
      }
      // sonst: weiter zum Server-Call unten
    }

    if (destinationFilter.length >= 1) {
      setIsLoading(true);
      const tenantId = window.configData?.AGENCYID;
      if (!tenantId) {
        console.warn("[SearchPopUp] No AGENCYID found, skipping search");
        return;
      }
      let apiCall = "/namesearch?";
      if (activeMode === "LTA") apiCall = "/searchpublicevents?";
      if (activeMode === "ACC" && agencyproperties.ibeProviderTag === "ratehawk") apiCall = "/searchhotels?ibeprovider=ratehawk&";

      axios
        .get(getHost() + apiCall + "query=" + encodeURIComponent(destinationFilter))
        .then((response) => {
          setIsLoading(false);
          const matches = response.data.matches && Array.isArray(response.data.matches) ? response.data.matches : [];

          // Cache speichern
          const parsed = parseMatches(matches);
          cachedResultsRef.current = { query: destinationFilter, matches, parsed };

          const groups = buildGroups(parsed, filterLower, regionGroupsFiltered);
          setLoadedOptions(groups);
        })
        .catch((error) => {
          console.error("Error loading options:", error);
          setLoadedOptions(regionGroupsFiltered.length > 0 ? [{ label: intl.formatMessage({ id: "travelGroups" }), options: regionGroupsFiltered }] : []);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      loadOptions(searchValue || "");
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [regionGroups, searchValue, isOpen]);

  const handleSelect = (option) => {
    // SINGLE SELECT MODE: Auto-submit on click
    let theSelection = [option];

    if (theSelection[0].RegionGroupID) delete theSelection[0].Region;
    if (theSelection[0].RegionID) delete theSelection[0].City;

    // Label für Anzeige im Suchfeld merken (Banner/Suggestion Label erhalten)
    if (option.label) {
      dispatch(setAlternativeRegionName(option.label));
    }

    // Handle suggestion types
    if (option.type === "suggestion") {
      suggestiontosearch(option, dispatch);
    }

    // Auto-submit and close
    if (onSelect) {
      onSelect(theSelection);
    }
    handleClose();
  };

  const handleClose = () => {
    setSearchValue("");
    if (onClose) {
      onClose();
    }
  };

  const formatOptionLabel = (opt) => {
    return (
      <div className="option-content">
        <div className="option-icon-wrapper">
          {opt.RegionGroupID ? (
            <FaMapPin className="option-icon" />
          ) : opt.RegionID ? (
            <FaMapMarkerAlt className="option-icon" />
          ) : opt.CityID ? (
            <FaCity className="option-icon" />
          ) : (
            <FaHome className="option-icon" />
          )}
        </div>
        <div className="option-details">
          <span className="option-title">
            {opt.label} {opt.villageName && <span className="option-hotelvlllage">({opt.villageName})</span>}
          </span>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="search-popup-overlay">
      <div className="search-popup-modal" ref={modalRef}>
        <div className="popup-header">
          <div className="popup-header-text">
            <span className="popup-title">{activeMode == "LTA" ? intl.formatMessage({ id: "chooseEvent" }) : intl.formatMessage({ id: "chooseDestination" })}</span>
            {/* <span className="popup-subtitle">
              {intl.formatMessage({ id: "multipleSelectionPossible" })}
            </span> */}
          </div>
          <button className="popup-close-button" onClick={handleClose}>
            <IoClose />
          </button>
        </div>

        <div className="popup-search">
          <IoSearch className="search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={activeMode == "LTA" ? intl.formatMessage({ id: "searchEvent" }) : intl.formatMessage({ id: "countryRegionOrHotel" })}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="search-input"
          />
          {searchValue && (
            <button className="search-clear" onClick={() => setSearchValue("")}>
              <IoClose />
            </button>
          )}
        </div>

        <div className="popup-content">
          {searchValue.trim() === "" ? (
            <div className="popular-destinations-container">
              {/* OBEN: Popular Destination Banners - volle Breite, nicht zu hoch */}
              {popularDestinationBanners.length > 0 && (
                <>
                  <h3 className="popular-section-title">{intl.formatMessage({ id: "popularDestinations" })}</h3>
                  <div className="popular-destinations-grid">
                    {popularDestinationBanners.map((banner, index) => (
                      <div
                        onClick={() => {
                          console.log(banner);
                          handleSelect({
                            SuggestID: banner.search_region_id, // search_region_id statt banner_id
                            label: banner.label,
                            Name: banner.label,
                            value: banner.label,
                          });
                        }}
                        key={banner.banner_id || index}
                        className="popular-destination-card"
                        dangerouslySetInnerHTML={{ __html: banner.html }}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* UNTEN: Suggestion Banners - 2 nebeneinander, nicht zu hoch */}
              {suggestionBanners.length > 0 && (
                <>
                  <h3 className="suggestion-section-title">{intl.formatMessage({ id: "ourRecommendations" })}</h3>
                  <div className="suggestion-cards-grid">
                    {suggestionBanners.map((banner, index) => (
                      <div
                        onClick={() => {
                          console.log(banner);
                          handleSelect({
                            SuggestID: banner.search_region_id, // search_region_id statt banner_id
                            label: banner.label,
                            Name: banner.label,
                            value: banner.label,
                          });
                        }}
                        key={banner.banner_id || index}
                        className="suggestion-card"
                        dangerouslySetInnerHTML={{ __html: banner.html }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : isLoading && loadedOptions.length === 0 ? (
            <div className="loading-message">{intl.formatMessage({ id: "loading" })}</div>
          ) : (
            <>
              {loadedOptions.map((group, groupIndex) => (
                <div key={groupIndex} className="search-section">
                  <h4 className="section-title">{group.label}</h4>
                  <div className="search-options">
                    {group.options.map((option, optionIndex) => (
                      <div key={`${groupIndex}-${optionIndex}`} className="search-option" onClick={() => handleSelect(option)}>
                        {formatOptionLabel(option)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {loadedOptions.length === 0 && !isLoading && <div className="no-results">{intl.formatMessage({ id: "noResultsFound" })}</div>}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SearchPopUp;
